import React, { useState, useEffect, useRef } from 'react';
import { api } from './api';
import FileTypeIcon from './FileTypeIcon';
import './AdminFileManager.css';
import './FileUpload.css';

export default function AdminFileManager() {
  const [filesBySection, setFilesBySection] = useState([]);
  const [filteredFilesBySection, setFilteredFilesBySection] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [diskSpace, setDiskSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [replacingFile, setReplacingFile] = useState(null);
  const [renamingFile, setRenamingFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Фильтрация файлов по поисковому запросу
    if (!searchQuery.trim()) {
      setFilteredFilesBySection(filesBySection);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = filesBySection.map(section => ({
        ...section,
        files: section.files.filter(file => {
          const fileName = ((file.display_name || file.file_name || '') + '').toLowerCase();
          return fileName.includes(query);
        })
      })).filter(section => section.files.length > 0);
      setFilteredFilesBySection(filtered);
    }
  }, [searchQuery, filesBySection]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [filesData, diskData] = await Promise.all([
        api.getFiles(),
        api.getDiskSpace(),
      ]);
      setFilesBySection(filesData);
      setFilteredFilesBySection(filesData);
      setDiskSpace(diskData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот файл?')) {
      return;
    }

    try {
      await api.deleteFile(fileId);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRename = async (fileId, currentFileName) => {
    if (!newFileName || !newFileName.trim()) {
      setError('Введите новое имя файла');
      return;
    }

    const trimmedName = newFileName.trim();
    if (trimmedName === currentFileName) {
      setRenamingFile(null);
      setNewFileName('');
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      console.log('Renaming file:', fileId, 'from:', currentFileName, 'to:', trimmedName);
      
      await api.renameFile(fileId, trimmedName);
      
      setRenamingFile(null);
      setNewFileName('');
      setSuccessMessage(`Файл переименован в "${trimmedName}"`);
      
      // Скрываем сообщение об успехе через 3 секунды
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      await loadData();
    } catch (err) {
      console.error('Error renaming file:', err);
      const errorMessage = err.message || 'Ошибка при переименовании файла';
      setError(errorMessage);
      alert(`Ошибка при переименовании файла: ${errorMessage}`);
    }
  };

  const handleReplace = async (fileId, file) => {
    if (!file) {
      setError('Файл не выбран');
      setSuccessMessage(null);
      return;
    }

    try {
      setReplacingFile(fileId);
      setError(null);
      setSuccessMessage(null);
      console.log('Replacing file:', fileId, 'with file:', file.name, 'size:', file.size, 'type:', file.type);
      
      const result = await api.replaceFile(fileId, file);
      console.log('File replaced successfully:', result);
      
      setReplacingFile(null);
      setSuccessMessage(`Файл "${file.name}" успешно заменен`);
      
      // Сбрасываем input после успешной замены
      const input = fileInputRefs.current[fileId];
      if (input) {
        input.value = '';
        console.log('Input value reset after successful replace');
      }
      
      // Скрываем сообщение об успехе через 3 секунды
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      await loadData();
    } catch (err) {
      console.error('Error replacing file:', err);
      const errorMessage = err.message || 'Ошибка при замене файла';
      setError(errorMessage);
      setSuccessMessage(null);
      setReplacingFile(null);
      
      // Показываем alert для более явного уведомления об ошибке
      alert(`Ошибка при замене файла: ${errorMessage}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="admin-loading">Загрузка файлов...</div>;
  }

  return (
    <div className="admin-file-manager">
      <div className="file-manager-header">
        <div className="admin-toolbar-actions">
          <div className="admin-search">
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Поиск файлов..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="admin-search-clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
        {diskSpace && (
          <div className="disk-space-info">
            <div className="disk-space-bar">
              <div
                className="disk-space-fill"
                style={{ width: `${diskSpace.used_percent}%` }}
              />
            </div>
            <div className="disk-space-text">
              Использовано: {diskSpace.used_gb} GB из {diskSpace.total_gb} GB
              ({diskSpace.used_percent}%)
            </div>
            <div className="disk-space-free">
              Свободно: {diskSpace.free_gb} GB
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="admin-error" style={{ 
          background: 'var(--eu-danger-bg)', 
          border: '1px solid var(--eu-danger-border)', 
          color: 'var(--eu-danger-text)', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '16px' 
        }}>
          ⚠️ {error}
        </div>
      )}
      
      {successMessage && (
        <div className="admin-success" style={{ 
          background: 'var(--eu-success-bg)', 
          border: '1px solid var(--eu-success-border)', 
          color: 'var(--eu-success-text)', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '16px' 
        }}>
          ✓ {successMessage}
        </div>
      )}

      {filteredFilesBySection.length === 0 ? (
        <div className="admin-empty">Нет файлов</div>
      ) : (
        <div className="files-by-section">
          {filteredFilesBySection.map((section) => (
            <div key={section.section_id || 'no-section'} className="section-files">
              <h4 className="section-title">
                {section.section_title}
                <span className="file-count">({section.files.length})</span>
              </h4>
              <div className="files-grid">
                {section.files.map((file) => (
                  <div
                    key={file.id}
                    className="file-item"
                    onMouseEnter={() => setHoveredFile(file.id)}
                    onMouseLeave={() => setHoveredFile(null)}
                  >
                    <div className="file-icon">
                      <FileTypeIcon fileName={file.file_name} fileType={file.file_type} size={40} />
                    </div>
                    <div className="file-name" title={file.file_name}>
                      {file.file_name}
                    </div>
                    {hoveredFile === file.id && (
                      <div className="file-actions">
                        <input
                          type="file"
                          id={`file-replace-${file.id}`}
                          ref={(input) => {
                            if (input) {
                              fileInputRefs.current[file.id] = input;
                              // Удаляем старый обработчик, если он был
                              const oldHandler = input._changeHandler;
                              if (oldHandler) {
                                input.removeEventListener('change', oldHandler);
                              }
                              // Добавляем новый обработчик
                              const changeHandler = (e) => {
                                const selectedFile = e.target.files?.[0];
                                console.log('File input onChange triggered (via addEventListener):', selectedFile);
                                if (selectedFile) {
                                  console.log('Calling handleReplace with fileId:', file.id, 'file:', selectedFile.name, 'size:', selectedFile.size);
                                  handleReplace(file.id, selectedFile);
                                } else {
                                  console.warn('No file selected in onChange');
                                }
                              };
                              input._changeHandler = changeHandler;
                              input.addEventListener('change', changeHandler);
                            }
                          }}
                          data-file-id={file.id}
                          style={{ display: 'none' }}
                          disabled={replacingFile === file.id}
                        />
                        <button
                          type="button"
                          className={`file-replace-button ${replacingFile === file.id ? 'disabled' : ''}`}
                          disabled={replacingFile === file.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (replacingFile === file.id) {
                              return false;
                            }
                            console.log('Button clicked, triggering file input');
                            const input = fileInputRefs.current[file.id];
                            if (input) {
                              console.log('Input found, triggering click');
                              input.click();
                            } else {
                              console.warn('Input not found in refs');
                            }
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          {replacingFile === file.id ? 'Замена...' : 'Изменить'}
                        </button>
                        <button
                          type="button"
                          className="file-action-button file-action-rename"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRenamingFile(file.id);
                            setNewFileName(file.file_name || '');
                          }}
                        >
                          📝 Переименовать
                        </button>
                        <button
                          className="file-action-button file-action-delete"
                          onClick={() => handleDelete(file.id)}
                        >
                          🗑️ Удалить
                        </button>
                      </div>
                    )}
                    {renamingFile === file.id && (
                      <div className="file-rename-form">
                        <input
                          type="text"
                          value={newFileName}
                          onChange={(e) => setNewFileName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRename(file.id, file.file_name);
                            } else if (e.key === 'Escape') {
                              setRenamingFile(null);
                              setNewFileName('');
                            }
                          }}
                          placeholder="Введите новое имя файла (с расширением)"
                          autoFocus
                          className="file-rename-input"
                        />
                        <div className="file-rename-hint">
                          <small style={{ color: 'var(--eu-text-muted)', fontSize: '12px' }}>
                            Укажите полное имя файла, включая расширение (например: document.pdf)
                          </small>
                        </div>
                        <div className="file-rename-buttons">
                          <button
                            type="button"
                            className="file-rename-save"
                            onClick={() => handleRename(file.id, file.file_name)}
                          >
                            ✓ Сохранить
                          </button>
                          <button
                            type="button"
                            className="file-rename-cancel"
                            onClick={() => {
                              setRenamingFile(null);
                              setNewFileName('');
                            }}
                          >
                            ✕ Отмена
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

